/**
 * Created by razial on 22.02.2024
 */

connector.CODE_SUCCEED = 0;
connector.CODE_FAILED = 1;
connector.CODE_CANCELLED = 2;
connector.CODE_PENDING = 3;

connector.STATUS_CONNECTED = "connected";
connector.STATUS_DISCONNECTED = "disconnected";
connector.STATUS_DISABLED = "disabled";

connector.utils = {
    INTERVALS: {
        second: 1,
        minute: 60,
        hour: 3600,
        day: 86400,
        week: 7 * 86400,
        month: 30 * 86400
    },

    parseInterval: function (intervalStr) {
        const INTERVALS = connector.utils.INTERVALS;
        const value = parseInt(intervalStr) * 1000;

        for (const name in INTERVALS) {
            if (intervalStr.indexOf(name) !== -1) {
                const period = INTERVALS[name];
                return value * period;
            }
        }

        return value;
    },

    once: function (func) {
        return function (...args) {
            if (func) {
                const oldFunc = func;
                func = undefined;
                return oldFunc.call(this, ...args);
            }
        };
    },

    waitNoMore: function (timeout, callback) {
        let waitTimeout = setTimeout(() => {
            waitTimeout = undefined;
            callback();
        }, timeout);

        return function (...args) {
            if (waitTimeout !== undefined) {
                clearTimeout(waitTimeout);
                waitTimeout = undefined;
                callback.call(this, ...args);
            }
        };
    },

    accumulate: function (interval, callback) {
        let timeout = undefined;
        let self;
        let args;

        return function (...localArgs) {
            self = this;
            args = localArgs;

            if (timeout === undefined) {
                timeout = setTimeout(() => {
                    clearTimeout(timeout);
                    timeout = undefined;

                    const s = self, a = args;
                    self = undefined;
                    args = undefined;

                    callback.call(s, ...a);
                }, interval);
            }
        };
    },

    postpone: function (delay, callback) {
        let self;
        let args;
        let timeout;

        return function (...localArgs) {
            self = this;
            args = localArgs;

            clearTimeout(timeout);
            timeout = setTimeout(() => {
                callback.call(self, ...args);
            }, delay);
        };
    },

    wait: function (n, callback) {
        if (n === 0) {
            callback();
            return;
        }

        let total = 0;
        return function () {
            total++;
            if (total === n) {
                callback();
            }
        };
    },

    timeredThrottle: function (delay, callback) {
        let self, args, timeout;

        const exec = function () {
            if (timeout || !args) {
                return;
            }

            timeout = setTimeout(() => {
                timeout = undefined;
                exec();
            }, delay);

            const s = self, a = args;
            self = undefined;
            args = undefined;

            callback.call(s, ...a);
        };

        return function (...localArgs) {
            self = this;
            args = localArgs;

            exec();
        };
    },

    loadSdk: function (path, options) {
        options.onSuccessOnce = options.onSuccessOnce || connector.utils.once(options.onSuccess);
        options.onFailureOnce = options.onFailureOnce || connector.utils.once(options.onFailure);

        const onSuccess = options.onSuccessOnce;
        const onFailure = options.onFailureOnce;

        if (options.attempt === undefined) {
            options.attempt = 3;
        }

        const script = document.createElement("script");
        if (options.crossorigin !== false) {
            script.setAttribute("crossorigin", "anonymous");
        }
        script.setAttribute("type", "text/javascript");
        script.setAttribute("src", path);
        if (options.attrs) {
            Object.keys(options.attrs).forEach((attrName) => {
                script.setAttribute(attrName, options.attrs[attrName]);
            });
        }

        let loadTimeout;

        const onFailureCallback = function () {
            clearTimeout(loadTimeout);
            script.onload = undefined;
            script.onerror = undefined;

            if (options.attempt > 1) {
                if (document.body.contains(script)) {
                    document.body.removeChild(script);
                }
                options.attempt--;
                connector.utils.loadSdk(path, options);
            } else {
                onFailure();
            }
        };

        script.onload = function () {
            clearTimeout(loadTimeout);
            onSuccess();
        };
        script.onerror = onFailureCallback;
        loadTimeout = setTimeout(onFailureCallback, 30000);

        document.body.appendChild(script);
    },

    getCurrentScript: function () {
        if (document.currentScript) {
            return document.currentScript;
        }

        const scripts = document.getElementsByTagName("script");

        for (let i = 0; i < scripts.length; i++) {
            if (scripts[i].src && scripts[i].src.indexOf(connector.SDK_NAME) !== -1) {
                return scripts[i];
            }
        }
    },

    getRequestParameters: function (source, keepAsIs) {
        const res = {};
        let url = source || window.location.search;
        if (url) {
            url = url.substr(1); // Drop the leading '?' / '#'
            const nameValues = url.split("&");

            for (let i = 0; i < nameValues.length; i++) {
                const nameValue = nameValues[i].split("=");
                const name = nameValue[0];
                let value = nameValue[1] || "";
                if (!keepAsIs) {
                    try {
                        value = decodeURIComponent(value.replace(/\+/g, " "));
                    } catch (e) {
                        console.error(`getRequestParameters decodeURIComponent - ${value}`);
                    }
                }
                res[name] = value;
            }
        }
        return res;
    },

    stringify: function (obj) {
        try {
            if (typeof obj === "object" && !(obj instanceof Error)) {
                return JSON.stringify(obj);
            }
        } catch (e) {
            // do nothing
        }
        return String(obj);
    },

    isIFrame: function () {
        return window.top !== window.self;
    },

    clone: function (object, recursive) {
        if (object === null) {
            return null;
        }

        if (typeof object !== "object") {
            return object;
        }

        let r;

        if (Array.isArray(object)) {
            r = [];
            for (let i = 0; i < object.length; i++) {
                if (recursive) {
                    r.push(connector.utils.clone(object[i], true));
                } else {
                    r.push(object[i]);
                }
            }
            return r;
        }

        r = {};
        for (const name in object) {
            if (recursive) {
                r[name] = connector.utils.clone(object[name], true);
            } else {
                r[name] = object[name];
            }
        }
        return r;
    },

    setUrlHash: function (data) {
        const params = connector.utils.getRequestParameters(window.location.hash);

        for (const key in data) {
            const value = data[key];

            if (value === undefined) {
                delete params[key];
            } else {
                params[key] = value;
            }
        }

        window.location.hash = Object.keys(params).map((objectKey) => `${objectKey}=${params[objectKey]}`).join("&");
    },

    formatAmount: function (amount) {
        if (amount < 1000) {
            return amount;
        }

        const thousands = Math.floor(amount / 1000);

        amount -= thousands * 1000;

        if (amount < 10) {
            amount = `00${amount}`;
        } else if (amount < 100) {
            amount = `0${amount}`;
        }

        const DELIMETER = " ";
        return thousands + DELIMETER + amount;
    },

    substract: function (array1, array2, getKey) {
        getKey = getKey || function (item) {
            return item;
        };

        array1 = array1 || [];
        array2 = array2 || [];

        const array2Object = {};
        array2.forEach((item) => {
            array2Object[getKey(item)] = true;
        });

        return array1.filter((item) => array2Object[getKey(item)] === undefined);
    },

    loadBase64: function (url, callback) {
        if (url.startsWith("data:image")) {
            callback(url);
            return;
        }

        const img = new Image();
        img.crossOrigin = "anonymous";

        img.src = url;
        img.onload = function () {
            const canvas = document.createElement("canvas");
            canvas.height = img.naturalHeight;
            canvas.width = img.naturalWidth;
            canvas.getContext("2d").drawImage(img, 0, 0);

            callback(canvas.toDataURL("image/jpg"));
        };
        img.onerror = function (err) {
            console.log(`[connector] Error loading image:${url}`, err);

            callback();
        };
    },

    addSearchParams: function (url, searchParams) {
        const urlObj = new URL(url);
        for (const param in searchParams) {
            urlObj.searchParams.set(param, searchParams[param]);
        }
        return urlObj.toString();
    },

    hashCode: function (str) {
        if (str === undefined || str === null || typeof str === "number" && isNaN(str)) {
            return 0;
        }

        str += "";

        let hashCode = 0;

        for (let i = 0; i < str.length; i++) {
            // eslint-disable-next-line no-bitwise
            hashCode = ((hashCode << 5) - hashCode) + str.charCodeAt(i);
        }

        return hashCode;
    },

    throwAsync: function (message, e) {
        if (typeof message === "object" && message.group) {
            message = `REPORT: ${message.group.toUpperCase()} ${message.message}`;
        } else {
            const stack = (e || new Error()).stack;

            message = `${connector.utils.stringify(message)}${stack ? ` ${JSON.stringify(stack)}` : ""}`;
        }

        try {
            message = decodeURIComponent(message);
        } catch (error) {
            console.error(`[connector] throwAsync decodeURIComponent - ${message}`);
        }

        if (connector.platform.oneOf(connector.WECHAT)) {
            window.onerror(message);
        } else {
            setTimeout(() => {
                throw message;
            }, 0);
        }
    },

    postOnPause: function (url, data) {
        if (connector.platform.oneOf(connector.REDDIT)) {
            // every request there carries the devvit token in the authorization header, a beacon cannot
            return false;
        }

        const body = JSON.stringify(data);

        if (typeof Request !== "undefined" && "keepalive" in Request.prototype && new Blob([body]).size <= 65536) {
            fetch(url, {
                method: "POST",
                keepalive: true,
                headers: { "Content-Type": "text/plain" },
                body
            }).catch(() => {});
            return true;
        }

        if (typeof navigator !== "undefined" && navigator.sendBeacon) {
            return navigator.sendBeacon(url, body);
        }

        return false;
    }
};
